import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useEventContext } from '../lib/useEvent';
import { api, ApiError, type RegistrationResult } from '../lib/api';
import { Alert, LoadingBlock, Spinner } from '../components/ui';
import { CheckboxField, SelectField, TextAreaField, TextField } from '../components/Field';
import { formatMoney, formatSwimTimeShort } from '../lib/format';
import { GENDER_OPTIONS } from '../lib/types';

interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  confirmEmail: string;
  phone: string;
  dateOfBirth: string;
  gender: string;
  club: string;
  categoryId: string;
  seedMinutes: string;
  seedSeconds: string;
  emergencyContactName: string;
  emergencyContactPhone: string;
  medicalNotes: string;
  consentRules: boolean;
  consentMedia: boolean;
}

const EMPTY: FormState = {
  firstName: '',
  lastName: '',
  email: '',
  confirmEmail: '',
  phone: '',
  dateOfBirth: '',
  gender: '',
  club: '',
  categoryId: '',
  seedMinutes: '',
  seedSeconds: '',
  emergencyContactName: '',
  emergencyContactPhone: '',
  medicalNotes: '',
  consentRules: false,
  consentMedia: false,
};

type Errors = Partial<Record<keyof FormState | 'form', string>>;

const STEPS = ['About you', 'Your swim', 'Check and confirm'] as const;

/** Maps the server's field names back onto the form's field names. */
const SERVER_FIELD_MAP: Record<string, keyof FormState> = {
  firstName: 'firstName',
  lastName: 'lastName',
  email: 'email',
  phone: 'phone',
  dateOfBirth: 'dateOfBirth',
  gender: 'gender',
  club: 'club',
  categoryId: 'categoryId',
  seedTime: 'seedMinutes',
  emergencyContactName: 'emergencyContactName',
  emergencyContactPhone: 'emergencyContactPhone',
  medicalNotes: 'medicalNotes',
  consentRules: 'consentRules',
};

const EMAIL_RE = /^[^\s@]+@[^\s@.]+(?:\.[^\s@.]+)+$/;
const PHONE_RE = /^[+0-9][0-9\s()\-.]{6,24}$/;

export function RegisterPage() {
  const { event, loading, error: eventError } = useEventContext();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Errors>({});
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<RegistrationResult | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    // Clear the error for a field as soon as the user edits it.
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  const seedTimeMs = useMemo(() => {
    const minutes = Number(form.seedMinutes);
    const seconds = Number(form.seedSeconds || '0');
    if (!Number.isFinite(minutes) || !Number.isFinite(seconds)) return null;
    if (form.seedMinutes === '') return null;
    return minutes * 60_000 + seconds * 1000;
  }, [form.seedMinutes, form.seedSeconds]);

  const categoryOptions = useMemo(
    () => (event?.categories ?? []).map((c) => ({ value: c.id, label: c.label })),
    [event],
  );

  function validateStep(index: number): Errors {
    const next: Errors = {};
    if (!event) return next;

    if (index === 0) {
      if (!form.firstName.trim()) next.firstName = 'Enter your first name';
      if (!form.lastName.trim()) next.lastName = 'Enter your last name';
      if (!EMAIL_RE.test(form.email.trim())) next.email = 'Enter a valid email address';
      else if (form.email.trim().toLowerCase() !== form.confirmEmail.trim().toLowerCase()) {
        next.confirmEmail = 'The email addresses do not match';
      }
      if (!PHONE_RE.test(form.phone.trim())) next.phone = 'Enter a contact phone number';
      if (!form.dateOfBirth) next.dateOfBirth = 'Enter your date of birth';
      else {
        const age = ageOn(form.dateOfBirth, event.eventDate);
        if (age < event.minAge) {
          next.dateOfBirth = `You must be at least ${event.minAge} on the day of the event`;
        } else if (age > 110) {
          next.dateOfBirth = 'Check the date of birth';
        }
      }
      if (!form.gender) next.gender = 'Choose an option';
    }

    if (index === 1) {
      if (!form.categoryId) next.categoryId = 'Choose an entry category';
      if (seedTimeMs === null) next.seedMinutes = 'Enter your predicted time';
      else if (Number(form.seedSeconds || 0) > 59) next.seedSeconds = 'Seconds must be 0-59';
      else if (seedTimeMs < event.minSeedTimeMs) {
        next.seedMinutes = `Must be slower than ${formatSwimTimeShort(event.minSeedTimeMs)}`;
      } else if (seedTimeMs > event.maxSeedTimeMs) {
        next.seedMinutes = `Must be faster than ${formatSwimTimeShort(event.maxSeedTimeMs)}`;
      }
      if (!form.emergencyContactName.trim()) {
        next.emergencyContactName = 'Enter an emergency contact name';
      }
      if (!PHONE_RE.test(form.emergencyContactPhone.trim())) {
        next.emergencyContactPhone = 'Enter an emergency contact phone number';
      }
    }

    if (index === 2 && !form.consentRules) {
      next.consentRules = 'You must accept the event rules to enter';
    }

    return next;
  }

  function goToStep(index: number) {
    setStep(index);
    // Move focus to the heading so keyboard and screen reader users land in
    // the right place after the step changes.
    requestAnimationFrame(() => headingRef.current?.focus());
  }

  function onNext() {
    const found = validateStep(step);
    if (Object.keys(found).length > 0) {
      setErrors(found);
      return;
    }
    setErrors({});
    goToStep(step + 1);
  }

  async function onSubmit(formEvent: React.FormEvent) {
    formEvent.preventDefault();
    if (!event) return;

    // Re-check every step, not just the last one.
    const all: Errors = { ...validateStep(0), ...validateStep(1), ...validateStep(2) };
    if (Object.keys(all).length > 0) {
      setErrors(all);
      const firstBadStep = Object.keys(validateStep(0)).length > 0 ? 0 : Object.keys(validateStep(1)).length > 0 ? 1 : 2;
      goToStep(firstBadStep);
      return;
    }

    setSubmitting(true);
    setErrors({});
    try {
      const created = await api.register({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        dateOfBirth: form.dateOfBirth,
        gender: form.gender,
        club: form.club.trim(),
        categoryId: form.categoryId,
        seedTime: `${Number(form.seedMinutes)}:${String(Number(form.seedSeconds || 0)).padStart(2, '0')}`,
        emergencyContactName: form.emergencyContactName.trim(),
        emergencyContactPhone: form.emergencyContactPhone.trim(),
        medicalNotes: form.medicalNotes.trim(),
        consentRules: form.consentRules,
        consentMedia: form.consentMedia,
      });
      setResult(created);
      window.scrollTo({ top: 0 });
    } catch (err) {
      if (err instanceof ApiError) {
        const mapped: Errors = { form: err.message };
        for (const [serverField, message] of Object.entries(err.fields)) {
          const formField = SERVER_FIELD_MAP[serverField];
          if (formField) mapped[formField] = message;
        }
        setErrors(mapped);
        goToStep(0);
      } else {
        setErrors({ form: 'Something went wrong. Please try again.' });
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) return <LoadingBlock message="Loading entry form..." />;
  if (eventError || !event) {
    return (
      <Alert variant="error" title="Entry form unavailable">
        <p>{eventError ?? 'Please try again shortly.'}</p>
      </Alert>
    );
  }

  if (result) return <SuccessPanel result={result} contactEmail={event.contactEmail} />;

  if (!event.registrationOpen) {
    return (
      <Alert variant="info" title="Entries are not open">
        <p>
          {event.placesRemaining === 0
            ? 'All places have been taken.'
            : 'Entries for this event are currently closed.'}{' '}
          Email <a href={`mailto:${event.contactEmail}`}>{event.contactEmail}</a> if you think this
          is wrong.
        </p>
      </Alert>
    );
  }

  return (
    <div className="prose stack">
      <h1 tabIndex={-1} ref={headingRef}>
        Enter the {event.name}
      </h1>
      <p className="muted">
        {formatMoney(event.entryFeePence, event.currency)} entry &middot;{' '}
        {event.placesRemaining.toLocaleString('en-GB')} places remaining
      </p>

      <ol className="steps">
        {STEPS.map((label, index) => (
          <li
            key={label}
            aria-current={index === step ? 'step' : undefined}
            data-complete={index < step ? 'true' : 'false'}
          >
            {label}
          </li>
        ))}
      </ol>

      {errors.form && (
        <Alert variant="error" title="Your entry was not saved">
          <p>{errors.form}</p>
        </Alert>
      )}

      <form onSubmit={onSubmit} noValidate>
        {step === 0 && (
          <section className="card stack" aria-labelledby="step-about">
            <h2 id="step-about">About you</h2>
            <div className="form-grid">
              <TextField
                label="First name"
                required
                autoComplete="given-name"
                value={form.firstName}
                error={errors.firstName}
                onChange={(e) => set('firstName', e.target.value)}
              />
              <TextField
                label="Last name"
                required
                autoComplete="family-name"
                value={form.lastName}
                error={errors.lastName}
                onChange={(e) => set('lastName', e.target.value)}
              />
              <TextField
                label="Email address"
                type="email"
                required
                autoComplete="email"
                hint="Your entry reference is shown on screen and tied to this address."
                value={form.email}
                error={errors.email}
                onChange={(e) => set('email', e.target.value)}
              />
              <TextField
                label="Confirm email address"
                type="email"
                required
                autoComplete="email"
                value={form.confirmEmail}
                error={errors.confirmEmail}
                onChange={(e) => set('confirmEmail', e.target.value)}
              />
              <TextField
                label="Phone number"
                type="tel"
                required
                autoComplete="tel"
                value={form.phone}
                error={errors.phone}
                onChange={(e) => set('phone', e.target.value)}
              />
              <TextField
                label="Date of birth"
                type="date"
                required
                autoComplete="bday"
                hint={`You must be ${event.minAge} or over on the day of the event.`}
                value={form.dateOfBirth}
                error={errors.dateOfBirth}
                onChange={(e) => set('dateOfBirth', e.target.value)}
              />
              <SelectField
                label="Gender"
                required
                placeholder="Choose..."
                options={GENDER_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                value={form.gender}
                error={errors.gender}
                onChange={(e) => set('gender', e.target.value)}
              />
              <TextField
                label="Club or team"
                hint="Optional."
                autoComplete="organization"
                value={form.club}
                error={errors.club}
                onChange={(e) => set('club', e.target.value)}
              />
            </div>
          </section>
        )}

        {step === 1 && (
          <section className="card stack" aria-labelledby="step-swim">
            <h2 id="step-swim">Your swim</h2>
            <div className="form-grid">
              <SelectField
                label="Entry category"
                required
                wide
                placeholder="Choose..."
                options={categoryOptions}
                value={form.categoryId}
                error={errors.categoryId}
                onChange={(e) => set('categoryId', e.target.value)}
              />
            </div>

            <fieldset>
              <legend>
                Predicted time for {event.distanceMeters.toLocaleString('en-GB')} m
                <span className="required-marker" aria-hidden="true">
                  *
                </span>
              </legend>
              <p className="field-hint" style={{ marginBottom: 'var(--space-3)' }}>
                Used to seed you into a {event.seedingMode === 'waves' ? 'wave' : 'heat'} with
                swimmers of a similar pace. Between{' '}
                {formatSwimTimeShort(event.minSeedTimeMs)} and{' '}
                {formatSwimTimeShort(event.maxSeedTimeMs)}. Best guess is fine.
              </p>
              <div className="form-grid">
                <TextField
                  label="Minutes"
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={180}
                  required
                  value={form.seedMinutes}
                  error={errors.seedMinutes}
                  onChange={(e) => set('seedMinutes', e.target.value)}
                />
                <TextField
                  label="Seconds"
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={59}
                  value={form.seedSeconds}
                  error={errors.seedSeconds}
                  onChange={(e) => set('seedSeconds', e.target.value)}
                />
              </div>
              {seedTimeMs !== null && !errors.seedMinutes && !errors.seedSeconds && (
                <p className="faint" style={{ margin: 'var(--space-3) 0 0' }} aria-live="polite">
                  Predicted time: <strong className="mono">{formatSwimTimeShort(seedTimeMs)}</strong>
                </p>
              )}
            </fieldset>

            <fieldset>
              <legend>Emergency contact</legend>
              <div className="form-grid">
                <TextField
                  label="Contact name"
                  required
                  value={form.emergencyContactName}
                  error={errors.emergencyContactName}
                  onChange={(e) => set('emergencyContactName', e.target.value)}
                />
                <TextField
                  label="Contact phone"
                  type="tel"
                  required
                  value={form.emergencyContactPhone}
                  error={errors.emergencyContactPhone}
                  onChange={(e) => set('emergencyContactPhone', e.target.value)}
                />
              </div>
            </fieldset>

            <TextAreaField
              label="Medical conditions or medication"
              hint="Shared with the safety team only. Leave blank if none."
              maxLength={1000}
              value={form.medicalNotes}
              error={errors.medicalNotes}
              onChange={(e) => set('medicalNotes', e.target.value)}
            />
          </section>
        )}

        {step === 2 && (
          <section className="card stack" aria-labelledby="step-confirm">
            <h2 id="step-confirm">Check and confirm</h2>

            <dl className="grid grid-2" style={{ margin: 0 }}>
              <Summary label="Name" value={`${form.firstName} ${form.lastName}`.trim()} />
              <Summary label="Email" value={form.email} />
              <Summary label="Phone" value={form.phone} />
              <Summary label="Date of birth" value={form.dateOfBirth} />
              <Summary
                label="Category"
                value={categoryOptions.find((c) => c.value === form.categoryId)?.label ?? '-'}
              />
              <Summary
                label="Predicted time"
                value={seedTimeMs !== null ? formatSwimTimeShort(seedTimeMs) : '-'}
              />
              <Summary label="Club" value={form.club || 'None given'} />
              <Summary
                label="Emergency contact"
                value={`${form.emergencyContactName} ${form.emergencyContactPhone}`.trim()}
              />
            </dl>

            <button type="button" className="btn btn-ghost btn-sm" onClick={() => goToStep(0)}>
              Change these details
            </button>

            {/*
              The only public copy not driven by the event record, so it is
              fixed to this event being a pool swim. If the venue type ever
              changes, or you want organisers to word their own declaration,
              this needs a consentText field on EventConfig rather than an edit
              here.
            */}
            <CheckboxField
              label={
                <>
                  I have read and accept the event rules and safety instructions, and I confirm I am
                  fit to swim {event.distanceMeters.toLocaleString('en-GB')} m in a pool.
                </>
              }
              required
              checked={form.consentRules}
              error={errors.consentRules}
              onChange={(checked) => set('consentRules', checked)}
            />

            <CheckboxField
              label="I am happy for photos and video of me at the event to be used in event publicity."
              checked={form.consentMedia}
              onChange={(checked) => set('consentMedia', checked)}
            />

            <Alert variant="info" title="Payment">
              <p>
                Your place is held as <strong>pending</strong> until the organisers confirm payment
                of {formatMoney(event.entryFeePence, event.currency)} by bank transfer.
              </p>
              <p style={{ marginBottom: 0 }}>
                The account details and your entry reference are shown on the next screen, and again
                under <Link to="/my-entry">Find my entry</Link> whenever you need them.
              </p>
            </Alert>
          </section>
        )}

        <div className="row row-end" style={{ marginTop: 'var(--space-4)' }}>
          {step > 0 && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => goToStep(step - 1)}
              disabled={submitting}
            >
              Back
            </button>
          )}
          {step < STEPS.length - 1 ? (
            <button type="button" className="btn btn-primary" onClick={onNext}>
              Continue
            </button>
          ) : (
            <button type="submit" className="btn btn-primary btn-lg" disabled={submitting}>
              {submitting ? (
                <>
                  <Spinner label="Submitting" /> Submitting...
                </>
              ) : (
                'Submit entry'
              )}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

function Summary({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="field-label">{label}</dt>
      <dd style={{ margin: 0 }} className="muted">
        {value || '-'}
      </dd>
    </div>
  );
}

function SuccessPanel({
  result,
  contactEmail,
}: {
  result: RegistrationResult;
  contactEmail: string;
}) {
  return (
    <div className="prose stack">
      <Alert variant="success" title="Entry received">
        <p style={{ marginBottom: 0 }}>
          Thanks {result.entry.firstName}, you are on the list.
        </p>
      </Alert>

      <section className="card stack">
        <h1>Your entry reference</h1>
        <p
          className="mono"
          style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--deep)', margin: 0 }}
        >
          {result.entry.reference}
        </p>
        <p className="muted">
          Write this down. You will need it, along with your email address, to look up your entry
          and your start time.
        </p>

        <h2>Next: payment</h2>
        <p>
          <strong>
            Amount due: {formatMoney(result.payment.amountPence, result.payment.currency)}
          </strong>
        </p>
        {/* Newline separated by the API, so the line breaks have to survive. */}
        <p style={{ whiteSpace: 'pre-line' }}>{result.payment.instructions}</p>

        <h2>Your details</h2>
        <dl className="grid grid-2" style={{ margin: 0 }}>
          <Summary
            label="Name"
            value={`${result.entry.firstName} ${result.entry.lastName}`}
          />
          <Summary label="Email" value={result.entry.email} />
          <Summary label="Predicted time" value={result.entry.seedTimeFormatted} />
          <Summary label="Status" value="Pending payment confirmation" />
        </dl>

        <div className="row">
          <Link to="/my-entry" className="btn btn-primary">
            Find my entry
          </Link>
          <Link to="/" className="btn btn-secondary">
            Back to event details
          </Link>
        </div>

        <p className="faint" style={{ marginBottom: 0 }}>
          Something wrong? Email <a href={`mailto:${contactEmail}`}>{contactEmail}</a> quoting{' '}
          {result.entry.reference}.
        </p>
      </section>
    </div>
  );
}

/** Whole years old on the event date. Mirrors the backend calculation. */
function ageOn(dateOfBirth: string, referenceDate: string): number {
  const dob = new Date(`${dateOfBirth}T00:00:00Z`);
  const ref = new Date(`${referenceDate}T00:00:00Z`);
  if (Number.isNaN(dob.getTime()) || Number.isNaN(ref.getTime())) return 0;
  let age = ref.getUTCFullYear() - dob.getUTCFullYear();
  const monthDelta = ref.getUTCMonth() - dob.getUTCMonth();
  if (monthDelta < 0 || (monthDelta === 0 && ref.getUTCDate() < dob.getUTCDate())) age -= 1;
  return Math.max(0, age);
}
